import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  CheckCircle2,
  Download,
  KeyRound,
  Library,
  Link2,
  MapPinned,
  Megaphone,
  Plus,
  RefreshCw,
  ShieldCheck,
  Upload,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, useAuth } from "./auth";
import { LinkDirectory, MediaLibrary } from "./AdministrationWorkspace";
import { CommunityKnowledge } from "./CommunityWorkspace";
import { SalesPerformanceDashboard } from "./SalesPerformanceDashboard";

type Company = {
  id: string;
  code: string;
  legal_name_en: string;
  trade_name: string | null;
  licence_number: string | null;
  licence_jurisdiction: string | null;
  licence_expiry: string | null;
  licence_status: string;
  trn: string | null;
  vat_registered: boolean;
  base_currency: string;
  invoice_prefix: string | null;
  active: boolean;
};
type User = {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  login_mode: string;
  two_factor_mode: string;
  two_factor_enabled: boolean;
  workwise_linked: boolean;
  trade_name: string | null;
  legal_entity_id: string | null;
  must_change_password: boolean;
};
type Team = {
  id: string;
  name: string;
  business_module: string;
  trade_name: string | null;
  member_count: number;
  chat_channel_id: string;
  legal_entity_id: string | null;
  active: boolean;
};
type TeamMember = {
  user_id: string;
  team_role: string;
  display_name: string;
  email: string;
  status: string;
};
type Audit = {
  id: string;
  action: string;
  object_type: string;
  actor_name: string | null;
  actor_email: string | null;
  occurred_at: string;
};
type FeeRule = {
  id: string;
  fee_name: string;
  trade_name: string | null;
  revenue_scope: string;
  rate_percent: string;
  effective_from: string;
  effective_to: string | null;
  active: boolean;
};
type FeeAccrual = {
  id: string;
  fee_name: string;
  trade_name: string;
  period_start: string;
  period_end: string;
  sales_basis: string;
  rate_percent: string;
  fee_amount: string;
  currency: string;
  status: string;
};
type Integration = {
  id: string;
  legal_entity_id: string | null;
  connector: string;
  display_name: string;
  trade_name: string | null;
  status: string;
  credentials_configured: boolean;
  last_success_at: string | null;
  last_error: string | null;
  configuration: Record<string, unknown>;
  imported_leads: number;
  published_listings: number;
  last_lead_received_at: string | null;
  last_sync: Record<string, unknown> | null;
};
type View =
  | "overview"
  | "companies"
  | "users"
  | "teams"
  | "announcements"
  | "communities"
  | "media"
  | "links"
  | "integrations"
  | "platform-fees"
  | "commission-controls"
  | "audit"
  | "security";

const labels: Record<View, { title: string; copy: string }> = {
  overview: {
    title: "Administration",
    copy: "Every company, person, location, asset, integration and operating link in one controlled workspace.",
  },
  companies: {
    title: "Companies & licences",
    copy: "Legal entities, VAT registrations, licences and financial configuration.",
  },
  users: {
    title: "Users & access",
    copy: "Local access, Workwise enforcement, MFA policy and company scope.",
  },
  teams: {
    title: "Teams & automatic chat",
    copy: "Every team automatically receives a management-visible, audited channel.",
  },
  announcements: {
    title: "Announcements",
    copy: "Publish time-bound company or group messages to the live workspace ticker.",
  },
  communities: {
    title: "UAE locations & communities",
    copy: "Preloaded emirates, cities, areas and practical community knowledge for the sales team.",
  },
  media: {
    title: "Advertising media",
    copy: "Store, review and approve property, vehicle and service images before website or portal publishing.",
  },
  links: {
    title: "Platform links",
    copy: "One readable directory for every internal workspace and approved external service.",
  },
  integrations: {
    title: "Integrations",
    copy: "Connect portals, government services, communications, payments and data providers without exposing credentials.",
  },
  "platform-fees": {
    title: "Workwise platform fees",
    copy: "Configure the percentage of recorded sales and create auditable payables for each business.",
  },
  "commission-controls": {
    title: "Commission controls",
    copy: "Set the authorised MOU approver for each company and keep every change in the audit trail.",
  },
  audit: {
    title: "Security audit",
    copy: "Immutable authentication, access and administration events.",
  },
  security: {
    title: "Your security",
    copy: "Add authenticator-based two-factor protection to this account.",
  },
};
const singular: Record<"companies" | "users" | "teams", string> = {
  companies: "company",
  users: "user",
  teams: "team",
};

export function AdminConsole() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("view");
  const views: View[] = [
    "overview",
    "companies",
    "users",
    "teams",
    "announcements",
    "communities",
    "media",
    "links",
    "integrations",
    "platform-fees",
    "commission-controls",
    "audit",
    "security",
  ];
  const active: View = views.includes(requested as View)
    ? (requested as View)
    : "overview";
  const { csrfToken, user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [audit, setAudit] = useState<Audit[]>([]);
  const [feeRules, setFeeRules] = useState<FeeRule[]>([]);
  const [feeAccruals, setFeeAccruals] = useState<FeeAccrual[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState<View | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [importingUsers, setImportingUsers] = useState(false);
  const canImportUsers = ["SUPERADMIN", "GROUP_ADMIN", "COMPANY_ADMIN", "COO", "OWNER"].includes(String(user?.role)) || Boolean(user?.permissions?.includes("USERS_IMPORT"));
  async function downloadUserTemplate() {
    setError("");
    try {
      const response = await fetch("/api/v1/admin/users/import-template", { credentials: "include" });
      if (!response.ok) throw new Error("User template download failed.");
      const blob = await response.blob(), link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = "realco-user-import-template.xlsx"; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
    } catch (value) { setError(value instanceof Error ? value.message : "User template download failed"); }
  }
  async function importUsers(file: File) {
    if (!csrfToken) return;
    setImportingUsers(true); setError("");
    try {
      const response = await fetch("/api/v1/admin/users/import", { method: "POST", credentials: "include", headers: { "content-type": file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "x-csrf-token": csrfToken }, body: file });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || body.code || "User import failed");
      setError(`Imported ${body.data?.created || 0} users; skipped ${body.data?.skipped || 0}.`);
      setRefresh((x) => x + 1);
    } catch (value) { setError(value instanceof Error ? value.message : "User import failed"); }
    finally { setImportingUsers(false); }
  }
  useEffect(() => {
    if (
      params.get("create") === "1" &&
      ["companies", "users", "teams"].includes(active)
    ) {
      setModal(active);
      setParams({ view: active }, { replace: true });
    }
  }, [active, params, setParams]);
  useEffect(() => {
    setLoading(true);
    setError("");
    Promise.all([
      api<{ data: Company[] }>("/api/v1/admin/companies"),
      api<{ data: User[] }>("/api/v1/admin/users"),
      api<{ data: Team[] }>("/api/v1/admin/teams"),
      user?.role === "SUPERADMIN"
        ? api<{ data: Audit[] }>("/api/v1/admin/audit")
        : Promise.resolve({ data: [] }),
      user?.role === "SUPERADMIN"
        ? api<{ rules: FeeRule[]; accruals: FeeAccrual[] }>(
            "/api/v1/superadmin/platform-fees",
          )
        : Promise.resolve({ rules: [], accruals: [] }),
      api<{ data: Integration[] }>("/api/v1/admin/integrations"),
    ])
      .then(([c, u, t, a, f, i]) => {
        setCompanies(c.data);
        setUsers(u.data);
        setTeams(t.data);
        setAudit(a.data);
        setFeeRules(f.rules);
        setFeeAccruals(f.accruals);
        setIntegrations(i.data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [refresh, user?.role]);
  const stats = useMemo(
    () =>
      [
        [
          "Legal entities",
          companies.length,
          String(companies.filter((x) => x.active).length) + " active",
          Building2,
        ],
        [
          "Active users",
          users.filter((x) => x.status === "ACTIVE").length,
          String(users.filter((x) => x.two_factor_enabled).length) +
            " with 2FA",
          Users,
        ],
        [
          "Teams",
          teams.length,
          String(teams.length) + " chat channels",
          UsersRound,
        ],
        [
          "Workwise required",
          users.filter((x) => x.login_mode === "WORKWISE_REQUIRED").length,
          "Policy controlled",
          ShieldCheck,
        ],
      ] as const,
    [companies, users, teams],
  );
  const canAdd =
    active === "companies" || active === "users" || active === "teams";
  return (
    <div className="content admin-console">
      <section className="page-heading">
        <div>
          <span className="kicker">Administration · Live data</span>
          <h1>{labels[active].title}</h1>
          <p>{labels[active].copy}</p>
        </div>
        <div className="heading-actions">
          {active === "users" && canImportUsers && <><button className="button secondary" onClick={() => void downloadUserTemplate()}><Download size={16}/>Download Excel template</button><label className={`button secondary file-button ${importingUsers ? "disabled" : ""}`}><Upload size={16}/>{importingUsers ? "Uploading…" : "Upload users"}<input type="file" accept=".xlsx,.xls" disabled={importingUsers} onChange={(event) => { const file = event.target.files?.[0]; if (file) void importUsers(file); event.currentTarget.value = ""; }}/></label></>}
          <button
            className="button secondary"
            onClick={() => setRefresh((x) => x + 1)}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          {canAdd && (
            <button className="button primary" onClick={() => setModal(active)}>
              <Plus size={17} />
              Add {singular[active as keyof typeof singular]}
            </button>
          )}
        </div>
      </section>
      <section className="metrics">
        {stats.map(([label, value, detail, Icon]) => (
          <article key={label}>
            <Icon size={18} />
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{detail}</small>
          </article>
        ))}
      </section>
      <div className="admin-tabs" role="tablist">
        {views
          .filter(
            (v) =>
              !["audit", "platform-fees"].includes(v) ||
              user?.role === "SUPERADMIN",
          )
          .map((v) => (
            <button
              role="tab"
              aria-selected={active === v}
              className={active === v ? "active" : ""}
              onClick={() => setParams({ view: v })}
              key={v}
            >
              {v
                .split("-")
                .map((x) => x[0].toUpperCase() + x.slice(1))
                .join(" ")}
            </button>
          ))}
      </div>
      {error && <div className="form-error">{error}</div>}
      <section className={`panel table-panel admin-data admin-view-${active}`}>
        {loading ? (
          <div className="empty">Loading secure records…</div>
        ) : (
          <ConsoleTable
            view={active}
            companies={companies}
            users={users}
            teams={teams}
            audit={audit}
            feeRules={feeRules}
            feeAccruals={feeAccruals}
            integrations={integrations}
            csrfToken={csrfToken}
            onChanged={() => setRefresh((x) => x + 1)}
            onNavigate={(view) => setParams({ view })}
          />
        )}
      </section>
      <CreateModal
        kind={modal}
        companies={companies}
        teams={teams}
        csrfToken={csrfToken}
        onClose={() => setModal(null)}
        onCreated={() => {
          setModal(null);
          setRefresh((x) => x + 1);
        }}
      />
    </div>
  );
}

function ConsoleTable({
  view,
  companies,
  users,
  teams,
  audit,
  feeRules,
  feeAccruals,
  integrations,
  csrfToken,
  onChanged,
  onNavigate,
}: {
  view: View;
  companies: Company[];
  users: User[];
  teams: Team[];
  audit: Audit[];
  feeRules: FeeRule[];
  feeAccruals: FeeAccrual[];
  integrations: Integration[];
  csrfToken: string | null;
  onChanged: () => void;
  onNavigate: (view: View) => void;
}) {
  if (view === "overview")
    return (
      <AdminOverview
        companies={companies.length}
        users={users.length}
        teams={teams.length}
        integrations={integrations.length}
        onNavigate={onNavigate}
      />
    );
  if (view === "communities") return <CommunityKnowledge administrative />;
  if (view === "announcements")
    return <AnnouncementAdmin csrfToken={csrfToken} />;
  if (view === "media") return <MediaLibrary companies={companies} />;
  if (view === "links") return <LinkDirectory administrative />;
  if (view === "security") return <SecurityPanel csrfToken={csrfToken} />;
  if (view === "integrations")
    return (
      <IntegrationsPanel
        companies={companies}
        integrations={integrations}
        csrfToken={csrfToken}
        onChanged={onChanged}
      />
    );
  if (view === "platform-fees")
    return (
      <PlatformFeesPanel
        companies={companies}
        rules={feeRules}
        accruals={feeAccruals}
        csrfToken={csrfToken}
        onChanged={onChanged}
      />
    );
  if (view === "commission-controls")
    return (
      <CommissionControls
        companies={companies}
        users={users}
        csrfToken={csrfToken}
      />
    );
  if (view === "companies")
    return (
      <Table
        heads={[
          "Company",
          "Code",
          "Licence",
          "TRN / VAT",
          "Currency",
          "Status",
        ]}
        rows={companies.map((c) => [
          <CompanyDetailsButton
            key={c.id}
            company={c}
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
          c.code,
          c.licence_number || "Not entered",
          c.vat_registered ? c.trn || "VAT registered" : "Not registered",
          c.base_currency,
          <Status key={c.id} ok={c.active}>
            {c.active ? "Active" : "Inactive"}
          </Status>,
        ])}
      />
    );
  if (view === "users")
    return (
      <Table
        heads={[
          "User",
          "Company",
          "Role",
          "Login policy",
          "2FA policy",
          "Status",
          "Manage",
        ]}
        rows={users.map((u) => [
          <UserDetailsButton
            key={u.id}
            user={u}
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
          u.trade_name || "Group",
          u.role.replaceAll("_", " "),
          <UserPolicy
            key={`${u.id}-login`}
            user={u}
            field="loginMode"
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
          <UserPolicy
            key={`${u.id}-mfa`}
            user={u}
            field="twoFactorMode"
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
          <UserPolicy
            key={`${u.id}-status`}
            user={u}
            field="status"
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
          <UserAccessButton
            key={`${u.id}-access`}
            user={u}
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
        ])}
      />
    );
  if (view === "teams")
    return (
      <Table
        heads={["Team", "Business", "Company", "Members", "Chat", "Manage"]}
        rows={teams.map((t) => [
          t.name,
          t.business_module,
          t.trade_name || "Group",
          t.member_count,
          t.chat_channel_id ? "Auto-created" : "Pending",
          <TeamMembersButton
            key={t.id}
            team={t}
            users={users}
            companies={companies}
            csrfToken={csrfToken}
            onChanged={onChanged}
          />,
        ])}
      />
    );
  return (
    <Table
      heads={["Time", "Actor", "Action", "Object"]}
      rows={audit.map((a) => [
        new Date(a.occurred_at).toLocaleString(),
        a.actor_name || a.actor_email || "System",
        a.action.replaceAll(".", " · "),
        a.object_type,
      ])}
    />
  );
}

function AdminOverview({
  companies,
  users,
  teams,
  integrations,
  onNavigate,
}: {
  companies: number;
  users: number;
  teams: number;
  integrations: number;
  onNavigate: (view: View) => void;
}) {
  const cards: Array<[View, string, string, ReactNode, string]> = [
    [
      "companies",
      "Companies & licences",
      "Legal entities, VAT, licences, invoice settings and operational status.",
      <Building2 />,
      `${companies} configured`,
    ],
    [
      "users",
      "Users & rights",
      "Roles, company scope, local or Workwise login, 2FA and account status.",
      <Users />,
      `${users} accounts`,
    ],
    [
      "teams",
      "Teams & chat",
      "Membership, team leads and automatically managed chat channels.",
      <UsersRound />,
      `${teams} teams`,
    ],
    [
      "announcements",
      "Announcements",
      "Publish group or company messages to the live ticker.",
      <Megaphone />,
      "Audited publishing",
    ],
    [
      "communities",
      "UAE master data",
      "Cities, areas, communities and sales-ready location intelligence.",
      <MapPinned />,
      "7 emirates",
    ],
    [
      "media",
      "Media library",
      "Property, vehicle, service and brand images prepared for advertising.",
      <Library />,
      "Approval controlled",
    ],
    [
      "integrations",
      "Integrations",
      "Portals, payments, communications, identity and government services.",
      <ShieldCheck />,
      `${integrations} configured`,
    ],
    [
      "links",
      "All platform links",
      "Internal workspaces and approved external services in one directory.",
      <Link2 />,
      "Role visible",
    ],
    [
      "security",
      "Profile security",
      "Authenticator 2FA and personal account protection.",
      <KeyRound />,
      "Personal controls",
    ],
  ];
  return (
    <div className="admin-overview-grid">
      {cards.map(([view, title, copy, icon, status]) => (
        <button key={view} onClick={() => onNavigate(view)}>
          <span>{icon}</span>
          <div>
            <h3>{title}</h3>
            <p>{copy}</p>
            <small>{status}</small>
          </div>
        </button>
      ))}
    </div>
  );
}

function CompanyDetailsButton({
  company,
  csrfToken,
  onChanged,
}: {
  company: Company;
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api(`/api/v1/admin/companies/${company.id}`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({
          tradeName: d.tradeName || null,
          licenceNumber: d.licenceNumber || null,
          licenceJurisdiction: d.licenceJurisdiction || null,
          licenceExpiry: d.licenceExpiry || null,
          trn: d.trn || null,
          vatRegistered: d.vatRegistered === "on",
          active: d.active === "on",
          invoicePrefix: d.invoicePrefix || null,
        }),
      });
      onChanged();
      setOpen(false);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Company could not be updated",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button className="entity-detail-trigger" onClick={() => setOpen(true)}>
        <span>
          <Building2 />
        </span>
        <div>
          <strong>{company.trade_name || company.legal_name_en}</strong>
          <small>{company.legal_name_en}</small>
          <em>Open company record</em>
        </div>
      </button>
      {open && (
        <div
          className="drawer-backdrop"
          onMouseDown={(e) => e.target === e.currentTarget && setOpen(false)}
        >
          <aside className="record-drawer admin-entity-drawer">
            <header>
              <div>
                <span className="kicker">Administration · Company record</span>
                <h2>{company.trade_name || company.legal_name_en}</h2>
                <p>
                  {company.code} · {company.base_currency}
                </p>
              </div>
              <button className="icon-button" onClick={() => setOpen(false)}>
                <X />
              </button>
            </header>
            {error && <div className="form-error">{error}</div>}
            <form onSubmit={save}>
              <div className="form-grid">
                <label>
                  Legal name
                  <input value={company.legal_name_en} disabled />
                </label>
                <label>
                  Company code
                  <input value={company.code} disabled />
                </label>
                <label>
                  Trade name
                  <input
                    name="tradeName"
                    defaultValue={company.trade_name || ""}
                  />
                </label>
                <label>
                  Invoice prefix
                  <input
                    name="invoicePrefix"
                    defaultValue={company.invoice_prefix || ""}
                  />
                </label>
                <label>
                  Licence number
                  <input
                    name="licenceNumber"
                    defaultValue={company.licence_number || ""}
                  />
                </label>
                <label>
                  Licence jurisdiction
                  <input
                    name="licenceJurisdiction"
                    defaultValue={company.licence_jurisdiction || ""}
                  />
                </label>
                <label>
                  Licence expiry
                  <input
                    name="licenceExpiry"
                    type="date"
                    defaultValue={company.licence_expiry?.slice(0, 10) || ""}
                  />
                </label>
                <label>
                  TRN
                  <input name="trn" defaultValue={company.trn || ""} />
                </label>
                <label className="check">
                  <input
                    name="vatRegistered"
                    type="checkbox"
                    defaultChecked={company.vat_registered}
                  />
                  VAT registered
                </label>
                <label className="check">
                  <input
                    name="active"
                    type="checkbox"
                    defaultChecked={company.active}
                  />
                  Active company
                </label>
              </div>
              <section className="entity-control-note">
                <ShieldCheck />
                <div>
                  <strong>Controlled company record</strong>
                  <p>
                    Changes are written to the administration audit trail.
                    Deactivation preserves historical transactions, staff,
                    customers and reporting.
                  </p>
                </div>
              </section>
              <footer>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </button>
                <button className="button primary" disabled={busy}>
                  {busy ? "Saving…" : "Save company"}
                </button>
              </footer>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}

function AnnouncementAdmin({ csrfToken }: { csrfToken: string | null }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Array<Record<string, any>>>([]),
    [open, setOpen] = useState(false),
    [selected, setSelected] = useState<Record<string, any> | null>(null),
    [error, setError] = useState("");
  async function load() {
    try {
      setRows(
        (
          await api<{ data: Array<Record<string, any>> }>(
            "/api/v1/enterprise/announcements/manage",
          )
        ).data,
      );
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Announcements could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await api(
        selected
          ? `/api/v1/enterprise/announcements/${selected.id}`
          : "/api/v1/enterprise/announcements",
        csrfToken,
        {
          method: selected ? "PATCH" : "POST",
          body: JSON.stringify({
            title: d.title,
            body: d.body,
            severity: d.severity,
            startsAt: d.startsAt
              ? new Date(String(d.startsAt)).toISOString()
              : undefined,
            expiresAt: d.expiresAt
              ? new Date(String(d.expiresAt)).toISOString()
              : null,
            targetRoles: [],
            active: d.active === "on",
            ctaLabel: d.ctaLabel || null,
            ctaUrl: d.ctaUrl || null,
          }),
        },
      );
      setOpen(false);
      setSelected(null);
      await load();
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Announcement could not be published",
      );
    }
  }
  return (
    <div className="announcement-admin">
      <header className="knowledge-toolbar">
        <div>
          <span className="kicker">Audited workspace ticker</span>
          <h2>Live announcements</h2>
        </div>
        <button className="button primary" onClick={() => setOpen(true)}>
          <Megaphone />
          New announcement
        </button>
      </header>
      {error && <div className="form-error">{error}</div>}
      <div className="catalog-grid">
        {rows.map((row) => (
          <article key={row.id} className={!row.active ? "muted-card" : ""}>
            <span className={`status ${String(row.severity).toLowerCase()}`}>
              {row.severity}
            </span>
            <h3>{row.title}</h3>
            <p>{row.body}</p>
            <small>
              {row.created_by_name} ·{" "}
              {new Date(row.created_at).toLocaleString("en-AE")}
            </small>
            <div className="card-actions">
              <button
                className="button secondary small"
                onClick={() => {
                  setSelected(row);
                  setOpen(true);
                }}
              >
                Edit
              </button>
              {user?.role === "SUPERADMIN" && (
                <button
                  className="button danger small"
                  onClick={async () => {
                    if (
                      csrfToken &&
                      window.confirm(
                        "Delete this announcement and retain its audit record?",
                      )
                    ) {
                      await api(
                        `/api/v1/enterprise/announcements/${row.id}`,
                        csrfToken,
                        { method: "DELETE" },
                      );
                      await load();
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {open && (
        <div className="modal-backdrop">
          <form className="modal announcement-modal" onSubmit={create}>
            <header>
              <div>
                <span className="kicker">Leadership communication</span>
                <h2>
                  {selected ? "Edit announcement" : "Publish announcement"}
                </h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => {
                  setOpen(false);
                  setSelected(null);
                }}
              >
                <X />
              </button>
            </header>
            <div className="form-grid">
              <label>
                Title
                <input
                  name="title"
                  required
                  defaultValue={selected?.title || ""}
                />
              </label>
              <label>
                Priority
                <select
                  name="severity"
                  defaultValue={selected?.severity || "INFO"}
                >
                  <option>INFO</option>
                  <option>SUCCESS</option>
                  <option>WARNING</option>
                  <option>URGENT</option>
                </select>
              </label>
              <label>
                Starts
                <input
                  name="startsAt"
                  type="datetime-local"
                  step={900}
                  required
                  defaultValue={
                    selected?.starts_at
                      ? new Date(selected.starts_at).toISOString().slice(0, 16)
                      : new Date().toISOString().slice(0, 16)
                  }
                />
              </label>
              <label>
                Expires
                <input
                  name="expiresAt"
                  type="datetime-local"
                  step={900}
                  defaultValue={
                    selected?.expires_at
                      ? new Date(selected.expires_at).toISOString().slice(0, 16)
                      : ""
                  }
                />
              </label>
              <label className="span-2">
                Message
                <textarea
                  name="body"
                  rows={4}
                  required
                  defaultValue={selected?.body || ""}
                />
              </label>
              <label>
                Action label
                <input
                  name="ctaLabel"
                  defaultValue={selected?.cta_label || ""}
                  placeholder="Open working list"
                />
              </label>
              <label>
                Action URL
                <input
                  name="ctaUrl"
                  defaultValue={selected?.cta_url || ""}
                  placeholder="/working-list"
                />
              </label>
              <label className="announcement-visible">
                <input
                  name="active"
                  type="checkbox"
                  defaultChecked={selected?.active ?? true}
                />
                <span>
                  <strong>Visible in ticker</strong>
                  <small>
                    Deactivate to retain the announcement and its complete audit
                    history without displaying it.
                  </small>
                </span>
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => {
                  setOpen(false);
                  setSelected(null);
                }}
              >
                Cancel
              </button>
              <button className="button primary">
                {selected ? "Save announcement" : "Publish"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function TeamMembersButton({
  team,
  users,
  companies,
  csrfToken,
  onChanged,
}: {
  team: Team;
  users: User[];
  companies: Company[];
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function load() {
    try {
      const result = await api<{ data: TeamMember[] }>(
        `/api/v1/admin/teams/${team.id}/members`,
      );
      setMembers(result.data);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to load members",
      );
    }
  }
  async function add(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(`/api/v1/admin/teams/${team.id}/members`, csrfToken, {
        method: "POST",
        body: JSON.stringify({ userId: d.userId, teamRole: d.teamRole }),
      });
      await load();
      onChanged();
    } catch (value) {
      setError(value instanceof Error ? value.message : "Unable to add member");
    } finally {
      setBusy(false);
    }
  }
  async function remove(userId: string) {
    if (
      !csrfToken ||
      !window.confirm(
        "Remove this user from the team and its automatic chat channel?",
      )
    )
      return;
    setBusy(true);
    try {
      await api(`/api/v1/admin/teams/${team.id}/members/${userId}`, csrfToken, {
        method: "DELETE",
      });
      await load();
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to remove member",
      );
    } finally {
      setBusy(false);
    }
  }
  async function saveTeam(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const data = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api(`/api/v1/admin/teams/${team.id}`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({
          name: data.name,
          businessModule: data.businessModule,
          legalEntityId: data.legalEntityId || null,
          active: data.active === "on",
        }),
      });
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Team details could not be saved",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        className="table-action"
        onClick={() => {
          setOpen(true);
          void load();
        }}
      >
        Manage members
      </button>
      {open && (
        <div className="modal-backdrop">
          <section className="modal team-members-modal">
            <header>
              <div>
                <span className="kicker">Automatic team chat</span>
                <h2>{team.name}</h2>
              </div>
              <button className="icon-button" onClick={() => setOpen(false)}>
                <X />
              </button>
            </header>
            {error && <div className="form-error">{error}</div>}
            <form className="team-edit-form" onSubmit={saveTeam}>
              <label>
                Team name
                <input name="name" defaultValue={team.name} required />
              </label>
              <label>
                Business module
                <input
                  name="businessModule"
                  defaultValue={team.business_module}
                  required
                />
              </label>
              <label>
                Company
                <select
                  name="legalEntityId"
                  defaultValue={team.legal_entity_id || ""}
                >
                  <option value="">Group-wide</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.trade_name || company.legal_name_en}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check">
                <input
                  name="active"
                  type="checkbox"
                  defaultChecked={team.active}
                />
                Active team
              </label>
              <button className="button secondary" disabled={busy}>
                Save team details
              </button>
            </form>
            <form className="member-add" onSubmit={add}>
              <label>
                User
                <select name="userId" required>
                  <option value="">Select user…</option>
                  {users
                    .filter(
                      (user) =>
                        !members.some((member) => member.user_id === user.id) &&
                        user.status === "ACTIVE",
                    )
                    .map((user) => (
                      <option value={user.id} key={user.id}>
                        {user.display_name} · {user.email}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Team role
                <select name="teamRole">
                  <option value="MEMBER">Member</option>
                  <option value="LEAD">Team lead</option>
                </select>
              </label>
              <button className="button primary" disabled={busy}>
                Add member
              </button>
            </form>
            <div className="member-list">
              {members.map((member) => (
                <article key={member.user_id}>
                  <span>
                    {member.display_name
                      .split(/\s+/)
                      .map((x) => x[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                  <div>
                    <strong>{member.display_name}</strong>
                    <small>
                      {member.email} · {member.team_role}
                    </small>
                  </div>
                  <button
                    disabled={busy}
                    onClick={() => void remove(member.user_id)}
                  >
                    Remove
                  </button>
                </article>
              ))}
              {!members.length && (
                <div className="empty">
                  No members yet. Add users to activate this team channel.
                </div>
              )}
            </div>
            <footer>
              <span className="modal-note">
                CEO/COO management access remains visible and audited.
              </span>
              <button
                className="button secondary"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}

function UserPolicy({
  user,
  field,
  csrfToken,
  onChanged,
}: {
  user: User;
  field: "loginMode" | "twoFactorMode" | "status";
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const current =
    field === "loginMode"
      ? user.login_mode
      : field === "twoFactorMode"
        ? user.two_factor_mode
        : user.status;
  const options =
    field === "loginMode"
      ? [
          ["LOCAL_ALLOWED", "Local allowed"],
          ["WORKWISE_REQUIRED", "Workwise required"],
        ]
      : field === "twoFactorMode"
        ? [
            ["OFF", "Off"],
            ["OPTIONAL", "Optional"],
            ["REQUIRED", "Required"],
          ]
        : [
            ["ACTIVE", "Active"],
            ["LOCKED", "Locked"],
            ["DISABLED", "Disabled"],
          ];
  async function change(value: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(`/api/v1/admin/users/${user.id}/policy`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value }),
      });
      onChanged();
    } catch (value) {
      window.alert(
        value instanceof Error ? value.message : "Policy update failed",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <select
      className="inline-policy"
      value={current}
      disabled={
        busy ||
        (field === "loginMode" &&
          !user.workwise_linked &&
          current === "LOCAL_ALLOWED")
      }
      onChange={(event) => void change(event.target.value)}
      title={
        !user.workwise_linked && field === "loginMode"
          ? "Connect the Workwise identity before requiring Workwise sign-in"
          : undefined
      }
    >
      {options.map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

function UserAccessButton({
  user,
  csrfToken,
  onChanged,
}: {
  user: User;
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<
    Array<{ code: string; label: string; module: string }>
  >([]);
  const [assigned, setAssigned] = useState<Array<{ permission_code: string }>>(
    [],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function load() {
    try {
      const [c, a] = await Promise.all([
        api<{ data: Array<{ code: string; label: string; module: string }> }>(
          "/api/v1/enterprise/admin/permissions/catalog",
        ),
        api<{ data: Array<{ permission_code: string }> }>(
          `/api/v1/enterprise/admin/users/${user.id}/permissions`,
        ),
      ]);
      setCatalog(c.data);
      setAssigned(a.data);
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "Access controls could not be loaded",
      );
    }
  }
  async function reset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const data = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api(
        `/api/v1/enterprise/admin/users/${user.id}/reset-password`,
        csrfToken,
        {
          method: "POST",
          body: JSON.stringify({
            temporaryPassword: data.password,
            confirmPassword: data.confirmPassword,
          }),
        },
      );
      onChanged();
      window.alert(
        "Temporary password set. The user must replace it at next login.",
      );
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Password reset failed",
      );
    } finally {
      setBusy(false);
    }
  }
  async function toggle(code: string, active: boolean) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(
        `/api/v1/enterprise/admin/users/${user.id}/permissions${active ? `/${code}` : ""}`,
        csrfToken,
        {
          method: active ? "DELETE" : "POST",
          body: active
            ? undefined
            : JSON.stringify({
                permissionCode: code,
                legalEntityId: user.legal_entity_id,
              }),
        },
      );
      await load();
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Permission update failed",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        className="table-action"
        onClick={() => {
          setOpen(true);
          void load();
        }}
      >
        Access & password
      </button>
      {open && (
        <div className="modal-backdrop">
          <section className="modal enterprise-modal">
            <header>
              <div>
                <span className="kicker">Audited people administration</span>
                <h2>{user.display_name}</h2>
                <p>
                  {user.email} · {user.role.replaceAll("_", " ")}
                </p>
              </div>
              <button className="icon-button" onClick={() => setOpen(false)}>
                <X />
              </button>
            </header>
            {error && <div className="form-error">{error}</div>}
            <form className="password-reset-admin" onSubmit={reset}>
              <h3>Set temporary password</h3>
              <p>
                At least 8 characters with uppercase, lowercase, number and
                symbol. The user will be required to change it at first login.
              </p>
              <div className="form-grid">
                <label>
                  Temporary password
                  <input
                    name="password"
                    type="password"
                    minLength={8}
                    required
                  />
                </label>
                <label>
                  Repeat password
                  <input
                    name="confirmPassword"
                    type="password"
                    minLength={8}
                    required
                  />
                </label>
              </div>
              <button
                className="button secondary"
                disabled={
                  busy ||
                  (user.role === "SUPERADMIN" && user.email !== "os@workw.com")
                }
              >
                <KeyRound size={15} />
                Reset password
              </button>
            </form>
            {user.role !== "SUPERADMIN" && (
              <section className="permission-matrix">
                <h3>Additional access rights</h3>
                <p>
                  Role access remains in place; these rights add explicitly
                  audited capabilities.
                </p>
                <div>
                  {catalog.map((permission) => {
                    const active = assigned.some(
                      (x) => x.permission_code === permission.code,
                    );
                    return (
                      <label key={permission.code}>
                        <input
                          type="checkbox"
                          checked={active}
                          disabled={busy}
                          onChange={() => void toggle(permission.code, active)}
                        />
                        <span>
                          <strong>{permission.label}</strong>
                          <small>
                            {permission.module} · {permission.code}
                          </small>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>
            )}
            <footer>
              <button
                className="button secondary"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}

function UserDetailsButton({
  user,
  csrfToken,
  onChanged,
}: {
  user: User;
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false),
    [detail, setDetail] = useState<Record<string, any> | null>(null),
    [error, setError] = useState(""),
    [editing, setEditing] = useState(false),
    [busy, setBusy] = useState(false);
  async function show() {
    setOpen(true);
    setError("");
    try {
      setDetail(
        (
          await api<{ data: Record<string, any> }>(
            `/api/v1/enterprise/admin/users/${user.id}/profile`,
          )
        ).data,
      );
    } catch (value) {
      setError(
        value instanceof Error
          ? value.message
          : "User profile could not be opened",
      );
    }
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken || !detail) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api(
        `/api/v1/enterprise/admin/users/${user.id}/profile`,
        csrfToken,
        {
          method: "PATCH",
          body: JSON.stringify({
            displayName: d.displayName,
            phone: d.phone || null,
            jobTitle: d.jobTitle || null,
            department: d.department || null,
            employeeNumber: d.employeeNumber || null,
            employmentType: d.employmentType || null,
            bio: d.bio || null,
            dateOfBirth: detail.date_of_birth || null,
            nationality: detail.nationality || null,
            gender: detail.gender || null,
            residentialAddress: detail.residential_address || null,
            employmentStartDate:
              detail.employment_start_date?.slice(0, 10) || null,
            employmentEndDate: detail.employment_end_date?.slice(0, 10) || null,
            languages: detail.languages || [],
            specialisms: detail.specialisms || [],
          }),
        },
      );
      setEditing(false);
      await show();
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "User could not be updated",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button className="user-detail-trigger" onClick={() => void show()}>
        <strong>{user.display_name}</strong>
        <small>
          {user.email}
          {user.workwise_linked ? " · Workwise linked" : ""}
          {user.must_change_password ? " · Password change due" : ""}
        </small>
        <span>Open full profile</span>
      </button>
      {open &&
        createPortal(
          <div
            className="drawer-backdrop"
            onMouseDown={(event) =>
              event.currentTarget === event.target && setOpen(false)
            }
          >
            <aside className="record-drawer enterprise-drawer">
              <header>
                <div>
                  <span className="kicker">Administration · User record</span>
                  <h2>{user.display_name}</h2>
                  <p>
                    {user.email} · {user.role.replaceAll("_", " ")}
                  </p>
                </div>
                <button className="icon-button" onClick={() => setOpen(false)}>
                  <X />
                </button>
              </header>
              {error && <div className="form-error">{error}</div>}
              {detail ? (
                <>
                  <div className="drawer-toolbar">
                    <button
                      className="button primary small"
                      onClick={() => setEditing((value) => !value)}
                    >
                      {editing ? "Cancel edit" : "Edit user details"}
                    </button>
                  </div>
                  {editing && (
                    <form className="admin-user-edit" onSubmit={save}>
                      <div className="form-grid">
                        <label>
                          Full name
                          <input
                            name="displayName"
                            defaultValue={detail.display_name}
                            required
                          />
                        </label>
                        <label>
                          Mobile
                          <input
                            name="phone"
                            defaultValue={detail.phone || ""}
                          />
                        </label>
                        <label>
                          Job title
                          <input
                            name="jobTitle"
                            defaultValue={detail.job_title || ""}
                          />
                        </label>
                        <label>
                          Department
                          <input
                            name="department"
                            defaultValue={detail.department || ""}
                          />
                        </label>
                        <label>
                          Employee number
                          <input
                            name="employeeNumber"
                            defaultValue={detail.employee_number || ""}
                          />
                        </label>
                        <label>
                          Employment type
                          <select
                            name="employmentType"
                            defaultValue={detail.employment_type || ""}
                          >
                            <option value="">Not recorded</option>
                            <option value="FULL_TIME">Full time</option>
                            <option value="PART_TIME">Part time</option>
                            <option value="CONTRACT">Contract</option>
                            <option value="CONSULTANT">Consultant</option>
                          </select>
                        </label>
                        <label className="span-2">
                          Professional profile
                          <textarea
                            name="bio"
                            rows={4}
                            defaultValue={detail.bio || ""}
                          />
                        </label>
                      </div>
                      <button className="button primary" disabled={busy}>
                        Save user
                      </button>
                    </form>
                  )}
                  <section className="record-facts">
                    {[
                      ["Company", detail.trade_name || "Group"],
                      ["Job title", detail.job_title || "Not recorded"],
                      ["Department", detail.department || "Not recorded"],
                      [
                        "Mobile",
                        detail.phone || detail.mobile || "Not recorded",
                      ],
                      ["Status", detail.status],
                      [
                        "Last login",
                        detail.last_login_at
                          ? new Date(detail.last_login_at).toLocaleString(
                              "en-AE",
                            )
                          : "Never",
                      ],
                    ].map(([label, value]) => (
                      <article key={label}>
                        <span>{label}</span>
                        <strong>{String(value)}</strong>
                      </article>
                    ))}
                  </section>
                  <SalesPerformanceDashboard userId={user.id} />
                  <section>
                    <h3>Profile and access</h3>
                    <p className="drawer-note">
                      Profile completion:{" "}
                      {Number(detail.completion_percent || 0)}
                      %. Changes, documents, team assignments and access
                      adjustments remain in the audit history.
                    </p>
                  </section>
                  <section>
                    <h3>Teams</h3>
                    <div className="tag-row">
                      {(detail.teams || []).map((team: Record<string, any>) => (
                        <span key={team.id}>
                          {team.name} · {team.team_role}
                        </span>
                      ))}
                      {!(detail.teams || []).length && (
                        <span>No team assigned</span>
                      )}
                    </div>
                  </section>
                </>
              ) : (
                <div className="empty">Loading complete profile…</div>
              )}
            </aside>
          </div>,
          document.body,
        )}
    </>
  );
}

function CommissionControls({
  companies,
  users,
  csrfToken,
}: {
  companies: Company[];
  users: User[];
  csrfToken: string | null;
}) {
  const [settings, setSettings] = useState<Array<Record<string, any>>>([]),
    [error, setError] = useState("");
  async function load() {
    try {
      setSettings(
        (
          await api<{ data: Array<Record<string, any>> }>(
            "/api/v1/enterprise/admin/commission-settings",
          )
        ).data,
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Commission settings could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function save(entityId: string, userId: string) {
    if (!csrfToken || !userId) return;
    try {
      await api(
        `/api/v1/enterprise/admin/commission-settings/${entityId}`,
        csrfToken,
        { method: "PUT", body: JSON.stringify({ mouApproverUserId: userId }) },
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Approver could not be saved");
    }
  }
  const approvers = users.filter((x) =>
    ["SUPERADMIN", "GROUP_ADMIN", "COO", "OWNER", "FINANCE"].includes(x.role),
  );
  return (
    <div className="commission-control-admin">
      <header className="admin-inline-heading">
        <div>
          <span className="kicker">UAE deal governance</span>
          <h2>MOU approval authority</h2>
          <p>
            Commission cannot become payable until the selected approver
            confirms the MOU and an internal approval and receipt exist.
          </p>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      <div className="admin-card-grid">
        {companies.map((company) => {
          const row = settings.find((x) => x.legal_entity_id === company.id);
          return (
            <article key={company.id}>
              <Building2 />
              <h3>{company.trade_name || company.legal_name_en}</h3>
              <label>
                Authorised MOU approver
                <select
                  defaultValue={row?.mou_approver_user_id || ""}
                  onChange={(e) => void save(company.id, e.target.value)}
                >
                  <option value="">Select approver…</option>
                  {approvers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name} · {user.role.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </label>
              <small>
                {row?.updated_at
                  ? `Last updated ${new Date(row.updated_at).toLocaleString("en-AE")}`
                  : "Not configured"}
              </small>
            </article>
          );
        })}
      </div>
    </div>
  );
}

const connectorOptions = [
  "WORKWISE",
  "PIXIX_CRM",
  "BAYUT",
  "PROPERTY_FINDER",
  "DUBIZZLE",
  "DLD_RERA",
  "TRAKHEESI",
  "EJARI",
  "UAE_PASS",
  "WHATSAPP",
  "EMAIL",
  "SMS",
  "META_ADS",
  "GOOGLE_ADS",
  "PAYMENT_GATEWAY",
  "BANK_FEED",
  "GPS_TELEMATICS",
  "HOLIDAY_CHANNEL",
];
function IntegrationsPanel({
  companies,
  integrations,
  csrfToken,
  onChanged,
}: {
  companies: Company[];
  integrations: Integration[];
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Integration | null>(null);
  const [connector, setConnector] = useState("BAYUT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  function openEditor(integration?: Integration) {
    setEditing(integration || null);
    setConnector(integration?.connector || "BAYUT");
    setError("");
    setOpen(true);
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    const configuration: dConfig = {};
    if (d.endpoint) configuration.endpoint = String(d.endpoint);
    if (d.senderEmail) configuration.senderEmail = String(d.senderEmail);
    if (d.senderName) configuration.senderName = String(d.senderName);
    if (d.agencyId) configuration.agencyId = String(d.agencyId);
    if (d.brokerOrn) configuration.brokerOrn = String(d.brokerOrn);
    if (d.tradeLicenseNumber) configuration.tradeLicenseNumber = String(d.tradeLicenseNumber);
    if (d.publicBaseUrl) configuration.publicBaseUrl = String(d.publicBaseUrl);
    if (d.apiBaseUrl) configuration.apiBaseUrl = String(d.apiBaseUrl);
    if (d.defaultLocationId) configuration.defaultLocationId = String(d.defaultLocationId);
    if (d.defaultPublicProfileId) configuration.defaultPublicProfileId = String(d.defaultPublicProfileId);
    if (d.providerFeedEndpoint) configuration.providerFeedEndpoint = String(d.providerFeedEndpoint);
    if (d.providerWebhookEndpoint) configuration.providerWebhookEndpoint = String(d.providerWebhookEndpoint);
    if (d.leadsApiEndpoint) configuration.leadsApiEndpoint = String(d.leadsApiEndpoint);
    configuration.leadSyncEnabled = d.leadSyncEnabled === "on";
    if (d.leadSyncIntervalMinutes) configuration.leadSyncIntervalMinutes = Number(d.leadSyncIntervalMinutes);
    if (d.leadBackfillHours) configuration.leadBackfillHours = Number(d.leadBackfillHours);
    if (d.portalEnvironment) configuration.environment = String(d.portalEnvironment);
    const credentials: Record<string, string> = {};
    if (d.apiKey) credentials.apiKey = String(d.apiKey);
    if (d.clientId) credentials.clientId = String(d.clientId);
    if (d.clientSecret) credentials.clientSecret = String(d.clientSecret);
    if (d.apiSecret) credentials.apiSecret = String(d.apiSecret);
    if (d.feedToken) credentials.feedToken = String(d.feedToken);
    if (d.listingToken) credentials.listingToken = String(d.listingToken);
    if (d.whatsappApiKey) credentials.whatsappApiKey = String(d.whatsappApiKey);
    if (d.leadsApiKey) credentials.leadsApiKey = String(d.leadsApiKey);
    if (d.leadWebhookToken) credentials.leadWebhookToken = String(d.leadWebhookToken);
    if (d.leadWebhookSecret) credentials.leadWebhookSecret = String(d.leadWebhookSecret);
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/admin/integrations", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          id: editing?.id,
          legalEntityId: d.legalEntityId || null,
          connector: d.connector,
          displayName: d.displayName,
          configuration,
          credentials: Object.keys(credentials).length
            ? credentials
            : undefined,
        }),
      });
      setOpen(false);
      setEditing(null);
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to save connector",
      );
    } finally {
      setBusy(false);
    }
  }
  async function test(id: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      const result = await api<{status:string;diagnostics?:Record<string,unknown>}>(`/api/v1/admin/integrations/${id}/test`, csrfToken, {
        method: "POST",
      });
      onChanged();
      const feed = result.diagnostics?.feedUrl ? `\nFeed URL: ${String(result.diagnostics.feedUrl)}` : "";
      const webhook = result.diagnostics?.leadWebhookUrl ? `\nLead webhook: ${String(result.diagnostics.leadWebhookUrl)}` : "";
      const signedPush = result.diagnostics?.signedPushUrl ? `\nBayut signed Push: ${String(result.diagnostics.signedPushUrl)} (${String(result.diagnostics?.signedPush || "not configured")})` : "";
      const pull = result.diagnostics?.leadsPull ? `\nBayut Pull: ${String(result.diagnostics.leadsPull)}` : "";
      window.alert(`Connection verified successfully.${feed}${webhook}${signedPush}${pull}`);
    } catch (value) {
      window.alert(
        value instanceof Error ? value.message : "Connection test failed",
      );
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function syncLeads(id: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      const result = await api<{fetched:number;imported:number;duplicates:number;rejected:number;metrics?:number;requestCount?:number}>(`/api/v1/admin/integrations/${id}/sync-leads`, csrfToken, { method:"POST" });
      window.alert(`Lead sync complete. Fetched ${result.fetched}; imported ${result.imported}; duplicates ${result.duplicates}; rejected ${result.rejected}; engagement metrics ${Number(result.metrics || 0)}.${result.requestCount ? ` Requests: ${result.requestCount}.` : ""}`);
      onChanged();
    } catch (value) {
      window.alert(value instanceof Error ? value.message : "Lead sync failed");
      onChanged();
    } finally { setBusy(false); }
  }
  const visibleIntegrations = integrations.filter((item) => item.connector !== "BREVO_EMAIL");
  return (
    <div className="integration-panel">
      <header className="fee-actions">
        <div>
          <span className="kicker">Encrypted connector vault</span>
          <h2>Property marketplace integrations</h2>
          <p>
            Publish properties and import enquiries from Bayut, Dubizzle and Property Finder through one encrypted control centre.
          </p>
        </div>
        <button className="button primary" onClick={() => openEditor()}>
          <Plus size={16} />
          Add integration
        </button>
      </header>
      <Table
        heads={[
          "Connector",
          "Name",
          "Company",
          "Credentials",
          "Listings / leads",
          "Last activity",
          "Status / action",
        ]}
        rows={visibleIntegrations.map((i) => [
          i.connector.replaceAll("_", " "),
          i.display_name,
          i.trade_name || "Group",
          i.credentials_configured ? "Encrypted" : "Not entered",
          `${Number(i.published_listings || 0)} published · ${Number(i.imported_leads || 0)} leads`,
          i.last_success_at
            ? new Date(i.last_success_at).toLocaleString()
            : "Never",
          <span key={i.id} className="integration-action">
            <Status ok={i.status === "CONNECTED"}>{i.status}</Status>
            <button disabled={busy} onClick={() => void test(i.id)}>
              Test
            </button>
            {["BAYUT","DUBIZZLE","PROPERTY_FINDER"].includes(i.connector) && <button disabled={busy || !i.configuration?.leadsApiEndpoint} onClick={() => void syncLeads(i.id)}>
              Pull leads
            </button>}
            <button disabled={busy} onClick={() => openEditor(i)}>
              Edit
            </button>
          </span>,
        ])}
      />
      {open && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={save}>
            <header>
              <div>
                <span className="kicker">Secure connection</span>
                <h2>{editing ? "Edit integration" : "Add integration"}</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => { setOpen(false); setEditing(null); }}
              >
                <X />
              </button>
            </header>
            {error && <div className="form-error">{error}</div>}
            <div className="form-grid">
              <label>
                Connector
                <select name="connector" value={connector} onChange={(event) => setConnector(event.target.value)}>
                  {connectorOptions.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <label>
                Display name
                <input
                  name="displayName"
                  required
                  defaultValue={editing?.display_name || (connector === "PROPERTY_FINDER" ? "Property Finder Enterprise API" : connector === "BAYUT" ? "Bayut Marketplace" : connector === "DUBIZZLE" ? "Dubizzle Marketplace" : "Provider integration")}
                />
              </label>
              <label>
                Company
                <select name="legalEntityId" defaultValue={editing?.legal_entity_id || ""}>
                  <option value="">Group-wide</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.trade_name || c.legal_name_en}
                    </option>
                  ))}
                </select>
              </label>
              <label>Portal environment<select name="portalEnvironment" defaultValue={String(editing?.configuration?.environment || "PRODUCTION")}><option value="SANDBOX">Sandbox / validation</option><option value="PRODUCTION">Production</option></select></label>
              {["BAYUT","DUBIZZLE","PROPERTY_FINDER"].includes(connector) && <>
                <label>Broker ORN<input name="brokerOrn" required defaultValue={String(editing?.configuration?.brokerOrn || "24413")} /></label>
                <label>Trade licence number<input name="tradeLicenseNumber" required defaultValue={String(editing?.configuration?.tradeLicenseNumber || "898346")} /></label>
                <label>Public website URL<input name="publicBaseUrl" type="url" required defaultValue={String(editing?.configuration?.publicBaseUrl || "https://realco.workw.com")} /></label>
                <label className="span-2">Leads API endpoint<input name="leadsApiEndpoint" type="url" defaultValue={String(editing?.configuration?.leadsApiEndpoint || (connector === "BAYUT" ? "https://www.bayut.com/api-v7/stats/website-client-leads" : ""))} placeholder="Provider-issued leads endpoint" /></label>
                <label>Leads API key<input name="leadsApiKey" type="password" placeholder={editing?.credentials_configured ? "Leave blank to keep current key" : "Provider-issued leads key"} /></label>
                {connector === "BAYUT" ? <label>Bayut Push secret<input name="leadWebhookSecret" type="password" minLength={16} placeholder={editing?.credentials_configured ? "Leave blank to keep current secret" : "Secret issued after webhook registration"} /></label> : <label>Inbound lead webhook token<input name="leadWebhookToken" type="password" minLength={16} placeholder={editing?.credentials_configured ? "Leave blank to keep current token" : "Create a random token (16+ characters)"} /></label>}
                <label>Lead sync interval<select name="leadSyncIntervalMinutes" defaultValue={String(editing?.configuration?.leadSyncIntervalMinutes || "15")}><option value="5">Every 5 minutes</option><option value="15">Every 15 minutes</option><option value="30">Every 30 minutes</option><option value="60">Hourly</option></select></label>
                <label>First sync history<select name="leadBackfillHours" defaultValue={String(editing?.configuration?.leadBackfillHours || "72")}><option value="24">Previous 24 hours</option><option value="72">Previous 3 days</option><option value="168">Previous 7 days</option><option value="720">Previous 30 days</option></select></label>
                <label className="check-line"><input name="leadSyncEnabled" type="checkbox" defaultChecked={Boolean(editing?.configuration?.leadSyncEnabled)} /> Enable scheduled lead import</label>
                {connector === "BAYUT" && <p className="span-2 field-hint">Push URL: https://realco.workw.com/api/v1/property-portals/leads/bayut · Signature header: X-Bayut-Signature</p>}
              </>}
              <label>Agency / partner ID<input name="agencyId" defaultValue={String(editing?.configuration?.agencyId || "")} placeholder="Provider-issued identifier" /></label>
              {["BAYUT","DUBIZZLE"].includes(connector) && <>
                <label>Secure XML feed token<input name="feedToken" type="password" required={!editing?.credentials_configured} placeholder={editing?.credentials_configured ? "Leave blank to keep current token" : "Minimum 16 characters"} /></label>
                <label>Bayut listing token<input name="listingToken" type="password" placeholder={editing?.credentials_configured ? "Leave blank to keep current token" : "Provider-issued listing token"} /></label>
                <label>WhatsApp API key<input name="whatsappApiKey" type="password" placeholder={editing?.credentials_configured ? "Leave blank to keep current key" : "Provider-issued WhatsApp key"} /></label>
                <label>Provider feed reference<input name="providerFeedEndpoint" type="url" defaultValue={String(editing?.configuration?.providerFeedEndpoint || (connector === "BAYUT" ? "https://pixxicrm.ae/api/sync/bayut/Realco" : "https://pixxicrm.ae/api/sync/dubizzle/Realco"))} /></label>
                <label className="span-2">Provider WhatsApp webhook reference<input name="providerWebhookEndpoint" type="url" defaultValue={String(editing?.configuration?.providerWebhookEndpoint || (connector === "DUBIZZLE" ? "https://pixxicrm.ae/int/v1/dubizzle/webhook/Realco" : "https://pixxicrm.ae/int/v1/bayut/webhook/Realco"))} /></label>
              </>}
              {connector === "PROPERTY_FINDER" && <>
                <label>Property Finder API base<input name="apiBaseUrl" type="url" defaultValue={String(editing?.configuration?.apiBaseUrl || "https://atlas.propertyfinder.com")} /></label>
                <label>API key<input name="apiKey" type="password" required={!editing?.credentials_configured} placeholder={editing?.credentials_configured ? "Leave blank to keep current key" : "Property Finder API key"} /></label>
                <label>API secret<input name="apiSecret" type="password" required={!editing?.credentials_configured} placeholder={editing?.credentials_configured ? "Leave blank to keep current secret" : "Property Finder API secret"} /></label>
                <label>Default Public Profile ID<input name="defaultPublicProfileId" defaultValue={String(editing?.configuration?.defaultPublicProfileId || "")} placeholder="Numeric agent profile ID" /></label>
                <label>Default Location ID<input name="defaultLocationId" defaultValue={String(editing?.configuration?.defaultLocationId || "")} placeholder="Numeric Property Finder location ID" /></label>
                <label className="span-2">Provider feed reference<input name="providerFeedEndpoint" type="url" defaultValue={String(editing?.configuration?.providerFeedEndpoint || "https://pixxicrm.ae/api/sync/propertyFinder/Realco")} /></label>
              </>}
              {!["BAYUT","DUBIZZLE","PROPERTY_FINDER"].includes(connector) && <label>
                HTTPS endpoint
                <input
                  name="endpoint"
                  type="url"
                  placeholder="Provider HTTPS endpoint"
                />
              </label>}
              {!["BAYUT","DUBIZZLE","PROPERTY_FINDER"].includes(connector) && <label>
                Integration API key / provider token
                <input
                  name="apiKey"
                  type="password"
                  autoComplete="off"
                  required={!editing?.credentials_configured}
                />
              </label>}
              {["EMAIL"].includes(connector) && <label>
                Sender email
                <input
                  name="senderEmail"
                  type="email"
                  placeholder="no-reply@workw.com"
                />
              </label>}
              {["EMAIL"].includes(connector) && <label>
                Sender name
                <input name="senderName" defaultValue="Workwise - Realco" />
              </label>}
              {!["BAYUT","DUBIZZLE","PROPERTY_FINDER","EMAIL"].includes(connector) && <label>
                Client ID
                <input name="clientId" autoComplete="off" />
              </label>}
              {!["BAYUT","DUBIZZLE","PROPERTY_FINDER","EMAIL"].includes(connector) && <label className="span-2">
                Client secret
                <input name="clientSecret" type="password" autoComplete="off" />
              </label>}
              <p className="span-2 modal-note">Keys and tokens are encrypted at rest and masked after saving. Bayut lead Pull uses the documented JSON API, signed Push verifies the raw request body, and listing-feed checks remain separate. Publishing is controlled per property and fully audited.</p>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => { setOpen(false); setEditing(null); }}
              >
                Cancel
              </button>
              <button className="button primary" disabled={busy}>
                Save securely
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
type dConfig = Record<string, unknown>;

function PlatformFeesPanel({
  companies,
  rules,
  accruals,
  csrfToken,
  onChanged,
}: {
  companies: Company[];
  rules: FeeRule[];
  accruals: FeeAccrual[];
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const first = `${today.slice(0, 8)}01`;
  async function addRule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/superadmin/platform-fees/rules", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          legalEntityId: d.legalEntityId || null,
          feeName: d.feeName,
          revenueScope: d.revenueScope,
          ratePercent: Number(d.ratePercent),
          effectiveFrom: d.effectiveFrom,
          effectiveTo: d.effectiveTo || null,
        }),
      });
      setOpen(false);
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to save fee rule",
      );
    } finally {
      setBusy(false);
    }
  }
  async function calculate() {
    if (!csrfToken) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/superadmin/platform-fees/calculate", csrfToken, {
        method: "POST",
        body: JSON.stringify({ periodStart: first, periodEnd: today }),
      });
      onChanged();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to calculate fees",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="fee-panel">
      <header className="fee-actions">
        <div>
          <span className="kicker">Superadmin commercial control</span>
          <h2>Percentage fee rules</h2>
        </div>
        <div>
          <button
            className="button secondary"
            onClick={() => void calculate()}
            disabled={busy}
          >
            Calculate current month
          </button>
          <button className="button primary" onClick={() => setOpen(true)}>
            <Plus size={16} />
            Add rule
          </button>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      <Table
        heads={["Rule", "Applies to", "Basis", "Rate", "Effective", "Status"]}
        rows={rules.map((r) => [
          r.fee_name,
          r.trade_name || "All businesses",
          r.revenue_scope.replaceAll("_", " "),
          `${Number(r.rate_percent)}%`,
          `${r.effective_from}${r.effective_to ? ` — ${r.effective_to}` : ""}`,
          <Status key={r.id} ok={r.active}>
            {r.active ? "Active" : "Inactive"}
          </Status>,
        ])}
      />
      <header className="fee-actions second">
        <div>
          <span className="kicker">Entity payables</span>
          <h2>Calculated accruals</h2>
        </div>
      </header>
      <Table
        heads={[
          "Period",
          "Business",
          "Sales basis",
          "Rate",
          "Workwise fee",
          "Status",
        ]}
        rows={accruals.map((a) => [
          `${a.period_start} — ${a.period_end}`,
          a.trade_name,
          new Intl.NumberFormat("en-AE", {
            style: "currency",
            currency: a.currency,
          }).format(Number(a.sales_basis)),
          `${Number(a.rate_percent)}%`,
          new Intl.NumberFormat("en-AE", {
            style: "currency",
            currency: a.currency,
          }).format(Number(a.fee_amount)),
          <Status key={a.id} ok={a.status === "POSTED"}>
            {a.status}
          </Status>,
        ])}
      />
      {open && (
        <div className="modal-backdrop">
          <form className="modal" onSubmit={addRule}>
            <header>
              <div>
                <span className="kicker">Workwise commercial model</span>
                <h2>Add platform fee rule</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="form-grid">
              <label>
                Rule name
                <input
                  name="feeName"
                  required
                  placeholder="Realco platform fee"
                />
              </label>
              <label>
                Business
                <select name="legalEntityId">
                  <option value="">All businesses</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.trade_name || c.legal_name_en}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Sales basis
                <select name="revenueScope">
                  <option value="ALL_SALES">All sales invoices</option>
                  <option value="NET_SALES">Net sales</option>
                  <option value="COLLECTED_SALES">Collected sales</option>
                </select>
              </label>
              <label>
                Rate percent
                <input
                  name="ratePercent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                  required
                />
              </label>
              <label>
                Effective from
                <input
                  name="effectiveFrom"
                  type="date"
                  required
                  defaultValue={today}
                />
              </label>
              <label>
                Effective to
                <input name="effectiveTo" type="date" />
              </label>
            </div>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button className="button primary" disabled={busy}>
                Save fee rule
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function Table({ heads, rows }: { heads: string[]; rows: ReactNode[][] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {heads.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && (
        <div className="empty">
          No records yet. Use Add to create the first record.
        </div>
      )}
    </div>
  );
}
function Status({ ok, children }: { ok: boolean; children: string }) {
  return (
    <span className={ok ? "status" : "status warning"}>
      <CheckCircle2 size={13} />
      {children}
    </span>
  );
}

function CreateModal({
  kind,
  companies,
  teams,
  csrfToken,
  onClose,
  onCreated,
}: {
  kind: View | null;
  companies: Company[];
  teams: Team[];
  csrfToken: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!kind || !["companies", "users", "teams"].includes(kind)) return null;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      if (kind === "companies")
        await api("/api/v1/admin/companies", csrfToken, {
          method: "POST",
          body: JSON.stringify({
            code: String(data.code).toUpperCase(),
            legalNameEn: data.legalNameEn,
            tradeName: data.tradeName,
            licenceNumber: data.licenceNumber,
            trn: data.trn,
            vatRegistered: data.vatRegistered === "on",
            baseCurrency: "AED",
          }),
        });
      if (kind === "users")
        await api("/api/v1/admin/users", csrfToken, {
          method: "POST",
          body: JSON.stringify({
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            legalEntityId: data.legalEntityId || null,
            teamId: data.teamId || null,
            temporaryPassword: data.temporaryPassword,
            twoFactorMode: data.twoFactorMode,
          }),
        });
      if (kind === "teams")
        await api("/api/v1/admin/teams", csrfToken, {
          method: "POST",
          body: JSON.stringify({
            name: data.name,
            businessModule: data.businessModule,
            legalEntityId: data.legalEntityId || null,
          }),
        });
      onCreated();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Could not save record",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form className="modal" onSubmit={submit}>
        <header>
          <div>
            <span className="kicker">Administration</span>
            <h2>Add {singular[kind as keyof typeof singular]}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {error && <div className="modal-error form-error">{error}</div>}
        <div className="form-grid">
          {kind === "companies" && (
            <>
              <label>
                Code
                <input name="code" required placeholder="COMPANY_CODE" />
              </label>
              <label>
                Trade name
                <input name="tradeName" required />
              </label>
              <label className="span-2">
                Legal name
                <input name="legalNameEn" required />
              </label>
              <label>
                Licence number
                <input name="licenceNumber" />
              </label>
              <label>
                TRN
                <input name="trn" />
              </label>
              <label className="check span-2">
                <input name="vatRegistered" type="checkbox" />
                VAT registered
              </label>
            </>
          )}
          {kind === "users" && (
            <>
              <label>
                Full name
                <input name="displayName" required />
              </label>
              <label>
                Email
                <input name="email" type="email" required />
              </label>
              <label>
                Role
                <select name="role">
                  <option value="STAFF">Company staff</option>
                  <option value="AGENT">Real-estate sales agent</option>
                  <option value="TEAM_LEAD">Team leader</option>
                  <option value="HR">Human resources</option>
                  <option value="OFFICE_MANAGER">Office manager</option>
                  <option value="FINANCE">Finance</option>
                  <option value="COMPANY_ADMIN">Company administrator</option>
                  <option value="GROUP_ADMIN">Group administrator</option>
                  <option value="COO">COO</option>
                  <option value="OWNER">Owner</option>
                  <option value="CUSTOMER">Customer</option>
                  <option value="FIELD">Field staff</option>
                </select>
              </label>
              <label>
                Company
                <select name="legalEntityId">
                  <option value="">Group-wide / customer</option>
                  {companies.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.trade_name || c.legal_name_en}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Initial team
                <select name="teamId">
                  <option value="">Select for agents…</option>
                  {teams.map((t) => (
                    <option value={t.id} key={t.id}>
                      {t.name} · {t.trade_name || "Group"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Temporary password
                <input
                  name="temporaryPassword"
                  type="password"
                  minLength={8}
                  required
                  placeholder="Minimum 8 characters"
                />
              </label>
              <label>
                2FA policy
                <select name="twoFactorMode">
                  <option value="OPTIONAL">Optional</option>
                  <option value="REQUIRED">Required</option>
                  <option value="OFF">Off</option>
                </select>
              </label>
            </>
          )}
          {kind === "teams" && (
            <>
              <label>
                Team name
                <input name="name" required />
              </label>
              <label>
                Business module
                <input
                  name="businessModule"
                  required
                  placeholder="REAL_ESTATE"
                />
              </label>
              <label className="span-2">
                Company
                <select name="legalEntityId">
                  <option value="">Group-wide</option>
                  {companies.map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.trade_name || c.legal_name_en}
                    </option>
                  ))}
                </select>
              </label>
              <p className="span-2 modal-note">
                A team chat channel will be created automatically. CEO/COO
                management access is visible and audited.
              </p>
            </>
          )}
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? "Saving…" : "Save record"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function SecurityPanel({ csrfToken }: { csrfToken: string | null }) {
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState("");
  async function setup() {
    setError("");
    try {
      const r = await api<{ secret: string; otpauthUri: string }>(
        "/api/v1/auth/mfa/setup",
        csrfToken,
        { method: "POST" },
      );
      setSecret(r.secret);
      setUri(r.otpauthUri);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Setup failed");
    }
  }
  async function confirm() {
    try {
      const r = await api<{ recoveryCodes: string[] }>(
        "/api/v1/auth/mfa/confirm",
        csrfToken,
        { method: "POST", body: JSON.stringify({ code }) },
      );
      setCodes(r.recoveryCodes);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Code not accepted");
    }
  }
  return (
    <div className="security-panel">
      <KeyRound size={28} />
      <div>
        <h2>Authenticator two-factor authentication</h2>
        <p>
          Use Google Authenticator, Microsoft Authenticator, 1Password or
          another TOTP application.
        </p>
        {error && <div className="form-error">{error}</div>}
        {!secret && !codes.length && (
          <button className="button primary" onClick={setup}>
            Begin secure setup
          </button>
        )}
        {secret && !codes.length && (
          <div className="security-setup">
            <label>
              Manual setup key
              <input readOnly value={secret} />
            </label>
            <details>
              <summary>Authenticator link</summary>
              <code>{uri}</code>
            </details>
            <label>
              Six-digit code
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                maxLength={6}
              />
            </label>
            <button className="button primary" onClick={confirm}>
              Confirm and enable
            </button>
          </div>
        )}
        {codes.length > 0 && (
          <div>
            <h3>Save these one-time recovery codes</h3>
            <div className="recovery-codes">
              {codes.map((c) => (
                <code key={c}>{c}</code>
              ))}
            </div>
            <p>They will not be shown again.</p>
          </div>
        )}
      </div>
    </div>
  );
}
